import type { LetterState } from '../engine/formulas'
import type { WordEntry } from '../engine/lexicon'
import type { LetterMastery } from '../engine/mastery'
import { GLOBAL } from '../config/global'

export interface BoardOperator {
  id: number
  symbol: string
  inputA: number
  inputB?: number
}

// Save schema. Bump SAVE_VERSION + add a migration when the shape changes.
export const SAVE_VERSION = 6
const KEY = 'incunabula:save'

export interface SaveData {
  version?: number
  ink: string // serialized Decimal
  letters: LetterState[]
  totalTaps: number
  lastSaved: number
  // v2 — Wordsmith
  lexicon: WordEntry[]
  quillCharge: number
  quillRegen: number // seconds accumulated toward the next charge
  // v3 — Forge Metrics
  edition: number
  heat: number
  weight: number
  totalLength: number
  unlockedSentences: number[]
  // v4 — Letter Mastery
  mastery: Record<number, LetterMastery>
  // v5 — Prestige & Operators
  type: number
  peakInk: number
  boardSlots: number
  activeLetterIds: number[]
  operators: BoardOperator[]
  typeface: Record<string, number>
  // v6 — Roguelike Challenges
  completedChallenges: number[]
}

// localStorage is used for the synchronous boot read (needed for offline calc).
// IndexedDB can be layered on later for larger payloads; the API stays the same.
export function saveGame(data: Omit<SaveData, 'version'>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ version: SAVE_VERSION, ...data }))
  } catch (e) {
    console.error('save failed', e)
  }
}

export function loadSave(): SaveData | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SaveData
    return migrate(parsed)
  } catch (e) {
    console.error('load failed', e)
    return null
  }
}

export function clearSave(): void {
  localStorage.removeItem(KEY)
}

function migrate(save: SaveData): SaveData {
  // v1 -> v2: introduce the Wordsmith fields with sensible defaults.
  if ((save.version ?? 1) < 2) {
    save.lexicon = save.lexicon ?? []
    save.quillCharge = save.quillCharge ?? GLOBAL.quillChargeMax
    save.quillRegen = save.quillRegen ?? 0
    save.version = 2
  }
  // v2 -> v3: introduce Forge Metrics fields.
  if ((save.version ?? 2) < 3) {
    save.edition = save.edition ?? 0
    save.heat = save.heat ?? 0
    save.weight = save.weight ?? 0
    save.totalLength = save.totalLength ?? 0
    save.unlockedSentences = save.unlockedSentences ?? []
    save.version = 3
  }
  // v3 -> v4: introduce Letter Mastery.
  if ((save.version ?? 3) < 4) {
    save.mastery = save.mastery ?? {}
    save.version = 4
  }
  // v4 -> v5: introduce Prestige & Board.
  if ((save.version ?? 4) < 5) {
    save.type = save.type ?? 0
    save.peakInk = save.peakInk ?? 0
    save.boardSlots = save.boardSlots ?? GLOBAL.startingSlots
    save.activeLetterIds = save.activeLetterIds ?? []
    save.operators = save.operators ?? []
    save.typeface = save.typeface ?? {}
    save.version = 5
  }
  // v5 -> v6: introduce Roguelike Challenges.
  if ((save.version ?? 5) < 6) {
    save.completedChallenges = save.completedChallenges ?? []
    save.version = 6
  }
  return save
}
